import { useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Loader2, MonitorPlay, ListChecks, KeyRound } from "lucide-react";
import { MediaLibrary } from "@/components/hub/MediaLibrary";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

const WIDGET_TYPES = [
  ["rules", "Rule cards (rotating)"],
  ["snapshot", "Live city snapshot"],
  ["queue", "Queue status"],
  ["game", "Reaction micro-game"],
  ["text", "Custom text card"],
  ["image", "Custom image"],
  ["discord", "Discord invite card"],
  ["tips", "Rotating tips"],
  ["countdown", "Event countdown"],
  ["socials", "Social links row"],
];

const newWidget = (type) => ({
  id: `w-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  type, enabled: true,
  props: type === "text" ? { title: "Welcome", body: "" }
    : type === "image" ? { url: "", caption: "" }
    : type === "discord" ? { url: "", label: "Join our Discord" }
    : type === "tips" ? { tips: [] }
    : type === "countdown" ? { label: "", at: "" }
    : type === "socials" ? { links: [] }
    : {},
});

/* Loading Studio — owners design their join screen: custom widgets (add/reorder/toggle),
   the entry-exam join gate, and a live preview of exactly what players see. */
export function LoadingStudio({ sid, widgets, setWidgets, gate, setGate, onSaveWidgets, onSaveGate, busy, onChanged }) {
  const [addType, setAddType] = useState("text");

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= widgets.length) return;
    const next = [...widgets];
    [next[i], next[j]] = [next[j], next[i]];
    setWidgets(next);
  };
  const setProp = (i, k, v) => setWidgets(widgets.map((w, wi) => (wi === i ? { ...w, props: { ...w.props, [k]: v } } : w)));

  const setQ = (i, patch) => setGate({ ...gate, questions: gate.questions.map((q, qi) => (qi === i ? { ...q, ...patch } : q)) });

  return (
    <div className="flex flex-col gap-5">
      {/* widget manager */}
      <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><MonitorPlay className="w-3.5 h-3.5" /> Loading screen widgets</p>
          <span className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em]">top = first card players see · max 8</span>
          <div className="ml-auto flex gap-2">
            <select data-testid="widget-add-type" value={addType} onChange={(e) => setAddType(e.target.value)} className="bg-[#050505] border border-white/15 px-2 py-2 font-mono text-[10px] focus:outline-none">
              {WIDGET_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button data-testid="widget-add" onClick={() => widgets.length < 8 && setWidgets([...widgets, newWidget(addType)])}
              className="bg-nc-orange text-black font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-2 flex items-center gap-1.5 hover:bg-nc-orange/80 transition-colors disabled:opacity-40" disabled={widgets.length >= 8}>
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
        </div>

        {widgets.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No custom widgets — the classic layout (rules / micro-game / queue) shows. Add widgets to take over the screen.</p>}

        {widgets.map((w, i) => (
          <div key={w.id} data-testid={`widget-${i}`} className="border border-white/10 bg-[#070707] p-4 flex flex-col gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-nc-orange border border-nc-orange/40 px-2 py-1">{WIDGET_TYPES.find(([t]) => t === w.type)?.[1] || w.type}</span>
              <label className="flex items-center gap-2 font-mono text-[9px] uppercase text-zinc-400 cursor-pointer">
                <input data-testid={`widget-enabled-${i}`} type="checkbox" checked={w.enabled} onChange={(e) => setWidgets(widgets.map((x, xi) => (xi === i ? { ...x, enabled: e.target.checked } : x)))} className="accent-nc-orange" /> visible
              </label>
              <span className="ml-auto flex gap-1">
                <button data-testid={`widget-up-${i}`} onClick={() => move(i, -1)} className="border border-white/15 p-1.5 text-zinc-400 hover:border-white/40 transition-colors"><ArrowUp className="w-3 h-3" /></button>
                <button data-testid={`widget-down-${i}`} onClick={() => move(i, 1)} className="border border-white/15 p-1.5 text-zinc-400 hover:border-white/40 transition-colors"><ArrowDown className="w-3 h-3" /></button>
                <button data-testid={`widget-del-${i}`} onClick={() => setWidgets(widgets.filter((_, xi) => xi !== i))} className="border border-red-500/40 p-1.5 text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-3 h-3" /></button>
              </span>
            </div>
            {w.type === "text" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><span className={labelCls}>Title</span><input data-testid={`widget-title-${i}`} className={inputCls} maxLength={80} value={w.props.title || ""} onChange={(e) => setProp(i, "title", e.target.value)} /></div>
                <div className="sm:col-span-2"><span className={labelCls}>Body</span><textarea data-testid={`widget-body-${i}`} rows={2} maxLength={500} className={`${inputCls} resize-none`} value={w.props.body || ""} onChange={(e) => setProp(i, "body", e.target.value)} /></div>
              </div>
            )}
            {w.type === "image" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><span className={labelCls}>Image URL (https)</span><input data-testid={`widget-img-${i}`} className={inputCls} maxLength={300} placeholder="https://…" value={w.props.url || ""} onChange={(e) => setProp(i, "url", e.target.value)} /></div>
                <div><span className={labelCls}>Caption</span><input className={inputCls} maxLength={120} value={w.props.caption || ""} onChange={(e) => setProp(i, "caption", e.target.value)} /></div>
              </div>
            )}
            {w.type === "discord" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><span className={labelCls}>Invite URL (https)</span><input data-testid={`widget-discord-${i}`} className={inputCls} maxLength={200} placeholder="https://discord.gg/…" value={w.props.url || ""} onChange={(e) => setProp(i, "url", e.target.value)} /></div>
                <div><span className={labelCls}>Button label</span><input className={inputCls} maxLength={60} value={w.props.label || ""} onChange={(e) => setProp(i, "label", e.target.value)} /></div>
              </div>
            )}
            {w.type === "tips" && (
              <div><span className={labelCls}>Tips — one per line, max 8</span><textarea data-testid={`widget-tips-${i}`} rows={3} className={`${inputCls} resize-none`} value={(w.props.tips || []).join("\n")} onChange={(e) => setProp(i, "tips", e.target.value.split("\n").filter((x) => x.trim()))} /></div>
            )}
            {w.type === "countdown" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><span className={labelCls}>Label</span><input className={inputCls} maxLength={60} placeholder="Season 2 launches in" value={w.props.label || ""} onChange={(e) => setProp(i, "label", e.target.value)} /></div>
                <div><span className={labelCls}>Date & time</span><input data-testid={`widget-countdown-${i}`} type="datetime-local" className={inputCls} value={w.props.at || ""} onChange={(e) => setProp(i, "at", e.target.value)} /></div>
              </div>
            )}
            {w.type === "socials" && (
              <div><span className={labelCls}>Links — "Label | https://url" per line, max 5</span><textarea data-testid={`widget-socials-${i}`} rows={3} className={`${inputCls} resize-none`} placeholder={"TikTok | https://tiktok.com/@yourserver\nYouTube | https://youtube.com/…"} value={(w.props.links || []).map((l) => `${l.label} | ${l.url}`).join("\n")}
                onChange={(e) => setProp(i, "links", e.target.value.split("\n").map((l) => { const [label, url] = l.split("|").map((x) => (x || "").trim()); return { label, url }; }).filter((l) => l.label && l.url))} /></div>
            )}
          </div>
        ))}

        <button data-testid="widgets-save" disabled={busy} onClick={onSaveWidgets} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 transition-colors w-fit flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MonitorPlay className="w-4 h-4" />} Publish widgets
        </button>
      </div>

      {/* media library */}
      <MediaLibrary sid={sid} onApplied={onChanged} />

      {/* join gate */}
      <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><ListChecks className="w-3.5 h-3.5" /> Entry exam (join gate)</p>
          <label className="flex items-center gap-2 font-mono text-[9px] uppercase text-zinc-300 cursor-pointer ml-auto">
            <input data-testid="gate-enabled" type="checkbox" checked={gate.enabled} onChange={(e) => setGate({ ...gate, enabled: e.target.checked })} className="accent-nc-orange" />
            Players must pass before entering
          </label>
        </div>
        <p className="font-mono text-[9px] text-zinc-500 leading-relaxed">Answered on your loading screen. The bridge checks the pass when a player connects — fail the exam, stay out of the city. Passes hold for 20 minutes.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <span className={labelCls}>Pass mark %</span>
          <input data-testid="gate-pass-min" type="number" min="0" max="100" className={`${inputCls} w-24`} value={gate.pass_min} onChange={(e) => setGate({ ...gate, pass_min: Number(e.target.value) })} />
          <span className={labelCls}>Min Citizen Level</span>
          <input data-testid="gate-min-level" type="number" min="0" max="50" className={`${inputCls} w-24`} value={gate.min_level ?? 0} onChange={(e) => setGate({ ...gate, min_level: Number(e.target.value) })} />
        </div>
        <p className="font-mono text-[8px] text-zinc-500 leading-relaxed -mt-2">Citizen Level gates hardcore servers: 0 = open to everyone. Above 0, players under the level are turned away at the loading screen with their current level shown — newcomers level up on open servers first.</p>
        {gate.questions.map((q, i) => (
          <div key={i} data-testid={`gate-q-${i}`} className="border border-white/10 bg-[#070707] p-4 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[9px] text-zinc-500">Q{i + 1}</span>
              <input data-testid={`gate-q-text-${i}`} className={inputCls} maxLength={300} placeholder="Question…" value={q.q} onChange={(e) => setQ(i, { q: e.target.value })} />
              <button data-testid={`gate-q-del-${i}`} onClick={() => setGate({ ...gate, questions: gate.questions.filter((_, qi) => qi !== i) })} className="border border-red-500/40 p-2 text-red-400 hover:bg-red-500/10 transition-colors shrink-0"><Trash2 className="w-3 h-3" /></button>
            </div>
            <div><span className={labelCls}>Choices — one per line (2–5)</span><textarea data-testid={`gate-q-choices-${i}`} rows={3} className={`${inputCls} resize-none`} value={(q.choices || []).join("\n")} onChange={(e) => setQ(i, { choices: e.target.value.split("\n").filter((x) => x.trim()) })} /></div>
            <div className="flex items-center gap-2">
              <span className={labelCls}>Correct answer</span>
              <select data-testid={`gate-q-answer-${i}`} className="bg-[#050505] border border-white/15 px-2 py-2 font-mono text-[10px]" value={q.answer} onChange={(e) => setQ(i, { answer: Number(e.target.value) })}>
                {(q.choices || []).map((c, ci) => <option key={ci} value={ci}>{ci + 1} — {String(c).slice(0, 40)}</option>)}
              </select>
            </div>
          </div>
        ))}
        <div className="flex gap-2 flex-wrap">
          <button data-testid="gate-add-q" onClick={() => gate.questions.length < 12 && setGate({ ...gate, questions: [...gate.questions, { q: "", choices: ["", ""], answer: 0 }] })}
            className="border border-white/15 text-zinc-300 font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-2.5 hover:border-white/40 transition-colors flex items-center gap-1.5" disabled={gate.questions.length >= 12}>
            <Plus className="w-3 h-3" /> Add question
          </button>
          <button data-testid="gate-save" disabled={busy} onClick={onSaveGate} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 transition-colors flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Save gate
          </button>
        </div>
      </div>

      {/* live preview */}
      <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3">
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase">Live preview — exactly what players see</p>
        <iframe data-testid="studio-preview" src={`/cef/loading/${sid}?pid=37049&name=Preview`} title="Loading screen preview" className="w-full aspect-video border border-white/10 bg-black" />
      </div>
    </div>
  );
}
